/**
 * The US-layout stand-in for a key the keyboard layout cannot name (review
 * FR1, narrowed by the orchestrator). The layout cannot name a code when the
 * map is unavailable, has no entry for it, or — on a NON-LATIN LETTER LAYOUT
 * only — has a non-ASCII entry for it. A non-Latin letter layout is a ready
 * map in which more than half of the 26 letter codes (`KeyA`–`KeyZ`, at least
 * `NON_LATIN_LETTER_THRESHOLD`) type a non-ASCII character: Chromium can
 * return the current Cyrillic, Greek, Hebrew… layout on Linux (KeyS → 'ы').
 * There a press still matches physical chords on `code`, and a letter, digit
 * or punctuation code stands for the key a US keyboard has there: logical
 * defaults such as Mod+S keep firing, and a physical chord at the Copy, Paste
 * or Undo position counts as that reserved chord.
 *
 * A Latin layout with a few non-ASCII keys (German ü ö ä ß, AZERTY é è ç à,
 * Nordic §) names those keys itself: they stay physical-only, with no US
 * stand-in, exactly as before FR1.
 *
 * No stand-in where the press cannot be that key: a digit or punctuation key
 * with Shift types its shifted character, which only the layout knows, and on
 * macOS Option changes `key` for every key (Option+F types ƒ).
 *
 * The dispatcher (`matchesEvent`), the reserved-chord pass and the collision
 * pass share this, so all three agree on which logical chord a press aliases.
 */
import type { ChordModifier, KeyboardLayoutSnapshot, Platform } from './keybindingTypes';

export type AliasModifiers = { readonly shift: boolean; readonly alt: boolean };

/** More than half of the 26 letter codes must type a non-ASCII character. */
export const NON_LATIN_LETTER_THRESHOLD = 14;

const LETTER_CODES: readonly string[] = Array.from({ length: 26 }, (_, index) => `Key${String.fromCharCode(65 + index)}`);
const PRINTABLE_ASCII = /^[\x20-\x7e]$/;
const US_PUNCTUATION: Readonly<Record<string, string>> = {
  Backquote: '`', Minus: '-', Equal: '=', BracketLeft: '[',
  BracketRight: ']', Backslash: '\\', Semicolon: ';', Quote: "'",
  Comma: ',', Period: '.', Slash: '/',
};

/** Layout snapshots are immutable; each is classified once. */
const nonLatinByLayout = new WeakMap<KeyboardLayoutSnapshot, boolean>();

/** A ready map whose letter keys mostly type non-Latin characters (see the header). */
export function isNonLatinLetterLayout(layout: KeyboardLayoutSnapshot): boolean {
  if (layout.status !== 'ready') return false;
  const known = nonLatinByLayout.get(layout);
  if (known !== undefined) return known;
  const nonAscii = LETTER_CODES.filter((code) => {
    const entry = layout.entries.get(code);
    return entry !== undefined && !PRINTABLE_ASCII.test(entry);
  }).length;
  const nonLatin = nonAscii >= NON_LATIN_LETTER_THRESHOLD;
  nonLatinByLayout.set(layout, nonLatin);
  return nonLatin;
}

/** Whether the layout cannot say which key `code` types (see the header). */
function layoutCannotName(code: string, layout: KeyboardLayoutSnapshot): boolean {
  if (layout.status === 'loading') return false;
  if (layout.status === 'unavailable') return true;
  const entry = layout.entries.get(code);
  if (entry === undefined) return true;
  return !PRINTABLE_ASCII.test(entry) && isNonLatinLetterLayout(layout);
}

/** The US-layout key of a letter, digit or punctuation code. */
export function usLayoutKeyForCode(code: string): string | null {
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit[0-9]$/.test(code)) return code.slice(5);
  return US_PUNCTUATION[code] ?? null;
}

export function aliasModifiersOf(modifiers: readonly ChordModifier[]): AliasModifiers {
  return { shift: modifiers.includes('Shift'), alt: modifiers.includes('Alt') };
}

/**
 * The logical key a press at `code` stands for when the layout cannot name
 * that code; null when the layout names it (callers ask `layoutTokenForCode`
 * for an ASCII name first) or when the press cannot be that key (Shift +
 * digit/punctuation, macOS Option).
 */
export function usLayoutAlias(
  code: string,
  modifiers: AliasModifiers,
  targetPlatform: Platform,
  layout: KeyboardLayoutSnapshot,
): string | null {
  if (!layoutCannotName(code, layout)) return null;
  if (modifiers.shift && !/^Key[A-Z]$/.test(code)) return null;
  if (targetPlatform === 'mac' && modifiers.alt) return null;
  return usLayoutKeyForCode(code);
}
